import { t } from "i18next";
import { useState, useEffect } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { PlanningWeek } from "../../../../interfaces/planning";

/**
 * Props for the TotalSessionsModal component
 * @property show - Controls modal visibility
 * @property week - The planning week being edited
 * @property onClose - Callback when modal is closed
 * @property onSave - Callback when value is saved
 */
interface TotalSessionsModalProps {
    show: boolean;
    week: PlanningWeek | null;
    onClose: () => void;
    onSave: (value: number) => void;
}

/**
 * Modal component for editing total sessions value in a planning week.
 * Simple modal with a single numeric input field.
 */
export function TotalSessionsModal(props: TotalSessionsModalProps) {
    // Form field
    const [value, setValue] = useState<string>('');

    /**
     * Load existing value when modal opens or week changes
     */
    useEffect(() => {
        if (props.week && props.week.total_sessions !== undefined && props.week.total_sessions !== null) {
            setValue(props.week.total_sessions.toString());
        } else {
            setValue('');
        }
    }, [props.week, props.show]);

    /**
     * Saves the value and calls parent callback
     */
    const handleSave = () => {
        const numValue = value.trim() === '' ? 0 : parseFloat(value);
        props.onSave(numValue);
        handleClose();
    };

    /**
     * Closes the modal and resets form
     */
    const handleClose = () => {
        props.onClose();
    };

    /**
     * Formats the date range for display in the modal header
     */
    const formatDateRange = () => {
        if (!props.week) return '';

        const fromDate = new Date(props.week.from);
        const toDate = new Date(props.week.to);

        const fromMonth = fromDate.toLocaleDateString('es-ES', { month: 'short' });
        const toMonth = toDate.toLocaleDateString('es-ES', { month: 'short' });

        const fromDay = props.week.from_name;
        const toDay = props.week.to_name;

        if (fromMonth === toMonth) {
            return `${fromDay} ${t("to")} ${toDay} de ${toMonth}`;
        } else {
            return `${fromDay} de ${fromMonth} ${t("to")} ${toDay} de ${toMonth}`;
        }
    };

    return (
        <Modal show={props.show} onHide={handleClose} size="sm" centered>
            <Modal.Header closeButton>
                <Modal.Title>
                    {t("Number of sessions")}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="mb-3">
                    <h6 className="text-muted">
                        {t("Week")} {props.week?.key}
                        {props.week && (
                            <small className="ms-2">
                                ({formatDateRange()})
                            </small>
                        )}
                    </h6>
                </div>

                <Form>
                    <Form.Group controlId="totalSessions">
                        <Form.Label>{t("Total sessions")}</Form.Label>
                        <Form.Control
                            type="number"
                            step="0.1"
                            value={value}
                            onChange={(e) => {
                                const val = e.target.value;
                                // Allow empty or valid numbers with max 1 decimal
                                if (val === '' || /^\d*\.?\d?$/.test(val)) {
                                    setValue(val);
                                }
                            }}
                            placeholder="0.0"
                            autoFocus
                        />
                        <Form.Text className="text-muted">
                            {t("Enter the total number of training sessions")}
                        </Form.Text>
                    </Form.Group>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose}>
                    {t("Cancel")}
                </Button>
                <Button variant="primary" onClick={handleSave}>
                    {t("Save")}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
